import { API_UNKNOWN_ERROR_MESSAGE } from "@/constants/common";
import { ERROR_STATUS_CODES } from "@/constants/error";
import { AppRoutes } from "@/routes/config";
import { TApiError } from "@/types/api";
import { logout } from "@/utils/logout";
import { toast } from "@/utils/toast";
import { createUrlWithParams } from "@/utils/url";
import { AxiosError } from "axios";
import Cookies from "js-cookie";
import { FieldValues, Path, UseFormReturn } from "react-hook-form";
import { ValidationError } from "yup";

/**
 * Type guard to determine if an error is of type TApiError.
 * @param error - The error to check.
 * @returns True if the error is a TApiError, otherwise false.
 */
export const isApiError = (error: unknown): error is TApiError => {
  return !!error && typeof error === "object" && "message" in error;
};

/**
 * Type guard to determine if an error is a Yup ValidationError.
 * @param error - The error to check.
 * @returns True if the error is a ValidationError, otherwise false.
 */
export const isValidatorError = (error: unknown): error is ValidationError => {
  return !!error && typeof error === "object" && "path" in error && "message" in error;
};

/**
 * Sets form errors based on the error type received from an API or Yup validation.
 * @param methods - React Hook Form methods for setting form errors.
 * @param error - The error to process.
 */
export const setErrorForm = <T extends FieldValues>(methods: UseFormReturn<T, object, undefined>, error: unknown) => {
  const { setError } = methods;

  if (isValidatorError(error)) {
    // Set Yup validation error
    setError(error.path as Path<T>, { message: error.message });
    return;
  }

  if (isApiError(error)) {
    // Set API error for each field if error contains an array of errors
    if (error.error && Array.isArray(error.error)) {
      error.error.forEach(({ property, message }) => {
        setError(property as Path<T>, { message });
      });
      return;
    }
  }
};

/**
 * Extracts a human-readable message from an API/unknown error, for inline display (e.g. alerts).
 * @param error - The error to read.
 * @returns The best-available error message string.
 */
export const getApiErrorMessage = (error: unknown): string => {
  if (isApiError(error) && (error.message || error.error)) {
    if (Array.isArray(error.error)) return error.error.map((e) => e.message).join("\n");
    if (typeof error.error === "string") return error.error;
    if (error.message) return error.message;
  }
  return API_UNKNOWN_ERROR_MESSAGE;
};

/**
 * Displays an error toast message based on the error type.
 * @param error - The error to display.
 */
export const showError = (error: unknown) => {
  if (isApiError(error) && (error.message || error.error)) {
    if (error.code === ERROR_STATUS_CODES.TOO_MANY_REQUEST_ERROR) {
      // Rate-limit errors carry the human message directly
      toast.error(error.message);
    } else if (error.error) {
      // Prefer field-level / detailed error payload when present
      if (Array.isArray(error.error)) {
        toast.error(error.error.map((e) => e.message).join("\n"));
      } else if (typeof error.error === "string") {
        toast.error(error.error);
      } else {
        toast.error(error.message);
      }
    } else if (error.message) {
      toast.error(error.message);
    }
  } else {
    // Show unknown error message in toast
    toast.error(API_UNKNOWN_ERROR_MESSAGE);
  }
};

/**
 * Handles API errors based on the error code and status.
 * Redirects users based on specific error codes.
 * @param error - The Axios error to handle.
 */
export const handleApiErrorWithCode = (error: AxiosError) => {
  if (error.code === "ERR_NETWORK") {
    setTimeout(() => {
      // Handle network error by redirecting to server error page
      window.location.replace(AppRoutes.serverErrorPage);
    }, 1000);
    return;
  }

  if (error.config?.method === "get") {
    // Handle GET request errors
    if (isApiError(error.response?.data)) {
      const { code, message } = error.response.data;
      toast.error(message);

      switch (code) {
        case ERROR_STATUS_CODES.COMPANY_NOT_EXISTS:
          window.location.replace(AppRoutes.home);
          return;
      }
    }
  }

  // Permission rejected by the server -> drop the local session and send the
  // user back to login (guard against looping when already on the login page).
  if (error.response?.status === 403 && isApiError(error.response?.data)) {
    const { code } = error.response.data;
    if (code === ERROR_STATUS_CODES.PERMISSION_DENIED) {
      logout().then((email) => {
        if (!window.location.pathname.startsWith(AppRoutes.login)) {
          window.location.replace(createUrlWithParams(AppRoutes.login, { email }));
        }
      });
      return;
    }
  }

  // Handle 404 errors for GET requests (skip if already on the not-found page)
  if (error.response?.status === 404 && error.config?.method === "get") {
    if (!window.location.pathname.startsWith(AppRoutes.pageNotFound)) {
      window.location.replace(AppRoutes.pageNotFound);
    }
  }

  if (error.response?.status === 500 && error.config?.method === "get") {
    if (!window.location.pathname.startsWith(AppRoutes.serverErrorPage)) {
      setTimeout(() => window.location.replace(AppRoutes.serverErrorPage), 1000);
    }
  }
};

export const getCustomSentryExceptionError = (error: AxiosError) => {
  const titleSentry = "Irohana Center Admin Error";
  const serverErrorResponse = error.response?.data as { message: string; code: string; error: unknown };
  let name = `[Irohana Center Admin]`;
  if (serverErrorResponse) {
    const customErrorResponseMessage = serverErrorResponse?.error
      ? typeof serverErrorResponse?.error === "string"
        ? serverErrorResponse?.error
        : JSON.stringify(serverErrorResponse?.error)
      : undefined;
    name = name + `: ${serverErrorResponse.message}`;
    const message = `${serverErrorResponse.code}\n${customErrorResponseMessage ?? ""}`;
    const user = { email: Cookies.get("admin-email") || Cookies.get("email") };
    const sendError = new Error(`${name} \n ${message} \n ${JSON.stringify(user)}`);
    sendError.name = titleSentry;
    return sendError;
  } else {
    const sendError = new Error(`${name} \n ${error.message}`);
    sendError.name = titleSentry;
    return sendError;
  }
};
